import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormsModule, UntypedFormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, filter, mergeMap, Observable } from 'rxjs';
import { MomentModule } from 'ngx-moment';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { IframeService } from '../../../../services/iframe-service';
import { ConnectionService } from '../../../../services/connection-service';
import { LocalStorage } from '../../../../services/local-storage-service';
import { DirectivesModule } from '../../../../components/directives.module';
import { TicketDetailsComponent } from '../ticket-details/ticket-details.component';
import { Broadcaster } from '../../../../components/broadcaster';
import { InputInternalUserSelectModule } from 'src/components/input/input-internal-user-select';

@Component({
  standalone: true,
  selector: 'app-ticket',
  templateUrl: './ticket.html',
  styleUrls: ['./ticket.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DirectivesModule,
    MomentModule,
    MatIcon,
    MatExpansionModule,
    TicketDetailsComponent,
    InputInternalUserSelectModule,
  ],
})
export class TicketsComponent implements OnInit {
  @Input() ticket: Table.SupportTicket;
  @Input() user: Table.User;
  @Input() showDuplicateCheckbox: boolean = false;
  @Input() isSelected: boolean = false;
  @Input() isDuplicateCheckboxEnabled: boolean = true;
  @Output() selectionChange = new EventEmitter<{ticket: Table.SupportTicket, selected: boolean}>();
  currentUser: Table.User;
  userRoles: Array<string>
  isExpanded: boolean = false;
  allocatedTo: string = '';
  allocationView: boolean;
  allocatedDoctor: Table.User;
  allocatedDoctorId: string;
  public autoCompleteDoctorController: UntypedFormControl = new UntypedFormControl();
  doctorsOptions: Observable<Array<{ name: string; object: Table.User }>>;
  skipReAllocateDoctor: boolean;
  constructor(
    private iframeService: IframeService,
    private conn: ConnectionService,
    private broadcastService: Broadcaster,
    private localStorage: LocalStorage
  ) {}

  ngOnInit(): void {
    this.currentUser = this.conn.getCurrentUser();
    this.userRoles = this.localStorage.getJsonValue('userRoles');
    this.getAllocatedOperator();
    this.doctorsOptions = this.autoCompleteDoctorController.valueChanges
      .pipe(
        debounceTime(300),
        filter((token: string) => !!token.length),
        mergeMap((token: string) => this.getDoctors(token)));
  }

  async getDoctors(name: string): Promise<Array<{ name: string; object: Table.User }>> {
    const doctors = await this.conn.findUsers({
      where: {
        DoctorDisplayName: { $regex: name, $options: 'i' },
        type: ApiClientConstant.User.Type.DOCTOR,
        inactive: { $ne: true },
      },
      limit: 5,
    });
    return doctors.map((doctor: Table.User): { name: string; object: Table.User } => ({
      name: this.getDoctorsDisplayName(doctor),
      object: doctor,
    }));
  }

  autoCompleteOnDoctorSelect(item: { name: string; object: Table.User }): void {
    this.allocatedDoctor = item.object;
    this.allocatedDoctorId = item.object.id;
    this.autoCompleteDoctorController.setValue('');
  }

  getDoctorsDisplayName(doctor: Table.User): string {
    return `${doctor?.get('DoctorDisplayName')} (${doctor.get('username')})`;
  }

  async assignTicket(): Promise<void> {
    if (this.allocatedDoctor) {
      this.ticket.set('allocatedDoctor', this.allocatedDoctor);
    }
    if (this.skipReAllocateDoctor) {
      this.ticket.set('skipReAllocateDoctor', this.skipReAllocateDoctor);
    }
    try {
      await this.ticket.save();
      this.getAllocatedOperator();
      this.broadcastService.broadcast('NOTIFY', { message: 'Ticket Assigned Successfully', type: ApiClientConstant.Toast.Type.SUCCESS });
    } catch (error) {
      this.broadcastService.broadcast('NOTIFY', { message: `Ticket assignment unsuccessfull ${error.message}`, type: ApiClientConstant.Toast.Type.ERROR });
    } finally {
      this.allocationView = false;
    }
  }

  toggleExpand(): void {
    if (!this.isExpanded) {
      this.updateIframe();
    }
    this.isExpanded = !this.isExpanded;
  }

  updateIframe(): void {
    this.iframeService.updateIframeUrl(`${this.conn
      .getWebAppUrl()}/chatV2/${this.ticket.id}?type=supportTicket&loginType=token&username=${this.user.get('username')}`);
  }

  getAllocatedOperator(): void {
    if (!this.ticket?.get('teams')) return;
    if (this.ticket?.get('teams')[0] === ApiClientConstant.Role.Name.DOCTOR) {
      this.allocatedTo = this.ticket?.get('allocatedDoctor')?.get('DoctorDisplayName') || '';
    } else {
      this.allocatedTo = this.ticket?.get('allocatedOperator')?.get('username') || '';
    }
  }

  canBeCaptured(): boolean {
    if (this.ticket?.get('status') === ApiClientConstant.SupportTicket.Status.Completed|| this.ticket?.get('status') === ApiClientConstant.SupportTicket.Status.RequestClosure) return false;
    if (!this.ticket?.get('teams')) return false;
    if (this.ticket.get('teams')[0] === ApiClientConstant.Role.Name.DOCTOR && this.currentUser?.get('type') === ApiClientConstant.User.Type.OPERATOR) return false;
    if (this.ticket.get('teams')[0] !== ApiClientConstant.Role.Name.DOCTOR && this.currentUser?.get('type') === ApiClientConstant.User.Type.DOCTOR) return false;
    if (this.ticket?.get('teams')[0] === ApiClientConstant.Role.Name.DOCTOR && this.ticket?.get('allocatedDoctor')) return false;
    if (this.ticket?.get('teams')[0] !== ApiClientConstant.Role.Name.DOCTOR && this.ticket?.get('allocatedOperator')) return false;
    return true;
  }s

  async captureTicket(): Promise<void> {
    if (this.currentUser?.get('type') === ApiClientConstant.User.Type.OPERATOR) {
      try {
        await this.ticket.save({ allocatedOperator: this.currentUser });
        this.broadcastService.broadcast('NOTIFY', {
          message: 'Ticket Captured',
          type: ApiClientConstant.Toast.Type.SUCCESS,
        });
      } catch (error) {
        this.broadcastService.broadcast('NOTIFY', {
          message: `Unable to capture ticket ${error.message}`,
          type: ApiClientConstant.Toast.Type.ERROR,
        });
      }
    } else {
      try {
        await this.ticket.save({ allocatedDoctor: this.currentUser });
        this.broadcastService.broadcast('NOTIFY', {
          message: 'Ticket Captured',
          type: ApiClientConstant.Toast.Type.SUCCESS,
        });
      } catch (error) {
        this.broadcastService.broadcast('NOTIFY', {
          message: `Unable to capture ticket ${error.message}`,
          type: ApiClientConstant.Toast.Type.ERROR,
        });
      }
    }
    await this.fetchTicket();
    this.getAllocatedOperator();
  }

  async fetchTicket(): Promise<void> {
    this.ticket = await this.conn.fetchSupportTicket({
      where: { objectId: this.ticket.id },
      include: ['titleLanguageString', 'allocatedOperator', 'allocatedDoctor'],
    });
  }

  switchToAlloactionView(): void {
    this.allocationView = true;
  }

  onSelectionChangeHandler(event: any): void {
    this.selectionChange.emit({ticket: this.ticket, selected: event.target.checked});
  }

  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
